import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Card, Typography } from "@mui/material";
import { Inline } from "components/layout/reusableLayouts";
import { DataTable, TextField } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import {
  DEBOUNCE_TIMEOUT,
  DEFAULT_PAGINATION_SIZE,
} from "../../common/Constants";
import TransactionStatus from "../../../enums/TransactionStatus";
import TransactionsType from "../../../enums/TransactionsType";
import { formatCashFull } from "../../../utils/formatCash";
import { GET_TRANSACTIONS } from "../../../graphql/transaction";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import dayjs from "utils/dayjs";
import RenderMerchantDisplay from "../../common/RenderMerchantDisplay";
import { fullName } from "utils/fullName";

type Data = {
  id: string;
  date: Date;
  merchant: string;
  url?: string;
  logo?: string;
  cachedLogo?: string;
  userName: string;
  amount: string;
  rewardAmount: string;
  type: TransactionsType;
};

interface RewardsHistoryTableProps {
  refreshDataIndex: number;
  handleOpenReviewDrawer: (transactionId: string) => void;
}

const RewardsTransactionTable: React.FC<RewardsHistoryTableProps> = (props) => {
  const { refreshDataIndex, handleOpenReviewDrawer } = props;

  const { t } = useTranslation();
  const [searchTermVariable, setSearchTermVairable] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const initialVariables = useMemo(
    () => ({
      first: DEFAULT_PAGINATION_SIZE,
      after: undefined,
      type: [TransactionsType.CAPTURE, TransactionsType.REFUND],
      transactionStatus: [
        TransactionStatus.COMPLETE,
        TransactionStatus.REFUNDED,
      ],
      ...(searchTermVariable?.length && { searchTerm: searchTermVariable }),
    }),
    [searchTermVariable],
  );

  const extractTransactions = (data: any): Data[] => {
    const firstKey = Object.keys(data)[0];
    return data[firstKey]?.edges.map((item: any) => ({
      id: item.node.id,
      merchant: item.node.merchant,
      url: item.node.merchantData.url,
      logo: item.node.merchantData.logo,
      cachedLogo: item.node.merchantData.cachedLogo,
      userName: fullName(item.node.user),
      amount: formatCashFull(item.node.amount, item.node.currency),
      rewardAmount:
        formatCashFull(item.node.rewardsAmount, item.node.currency) || "-",
      date: new Date(item.node.transactionDate),
      type: item.node.type,
    }));
  };

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_TRANSACTIONS,
      initialVariables,
      extractData: extractTransactions,
      refetchIndex: refreshDataIndex,
    });

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTermVairable(debouncedSearchTerm);
    }, DEBOUNCE_TIMEOUT);

    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearchTerm]);

  const columns: DataColumn<Data>[] = useMemo(
    () => [
      {
        id: "date",
        header: t("Date"),
        cell: (transaction) => dayjs(transaction.date).format("MM/DD/YYYY"),
      },
      {
        id: "merchant",
        header: t("Merchant"),
        cell: (transaction) => (
          <RenderMerchantDisplay
            merchant={transaction.merchant}
            url={transaction.url}
            logo={transaction.logo}
            cachedLogo={transaction.cachedLogo}
          />
        ),
      },
      {
        id: "userName",
        header: t("Name"),
        cell: (transaction) => transaction.userName,
      },
      {
        id: "amount",
        header: t("Transaction Amount"),
        align: "center",
        cell: (transaction) => ({
          content: transaction.amount,
          align: "right",
        }),
      },
      {
        id: "reward",
        header: t("Reward Amount"),
        align: "center",
        cell: (transaction) => ({
          content: transaction.rewardAmount,
          align: "right",
        }),
      },
      {
        id: "type",
        header: t("Type"),
        align: "center",
        cell: (transaction) => ({
          content:
            transaction.type === TransactionsType.REFUND
              ? t("Refund")
              : t("Purchase"),
          align: "right",
        }),
      },
    ],
    [t],
  );

  return (
    <Card
      variant="outlined"
      sx={{
        position: "relative",
        minWidth: 0,
        padding: "var(--space-5)",
      }}
    >
      <Inline
        wrap={false}
        justify="between"
        gap="0"
        sx={{ marginBottom: "var(--space-3)" }}
      >
        <Typography variant="h4">{t("Rewards By Transaction")}</Typography>
        <Box sx={{ width: "400px" }}>
          <TextField
            search
            fullWidth
            size="md"
            placeholder={t("Search Transactions")}
            inputProps={{ "aria-label": t("Search Transactions") }}
            value={debouncedSearchTerm}
            onChange={(e) => setDebouncedSearchTerm(e.target.value)}
            onClear={() => setDebouncedSearchTerm("")}
          />
        </Box>
      </Inline>
      <DataTable<Data>
        columns={columns}
        rows={dataArray}
        getRowKey={(transaction) => transaction.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={(transaction) => handleOpenReviewDrawer(transaction.id)}
        onScroll={handleScroll}
        maxHeight={350}
        emptyState={t("No transactions found")}
        loadingMoreText={t("Loading more transactions")}
        stickyHeader
      />
    </Card>
  );
};

export default RewardsTransactionTable;

// add where rewards went to
